import React, { useState, useEffect, useRef } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { markPdfAsAiGenerated } from '../lib/pdfMetadata';
import WaitlistModal from './WaitlistModal';
import PrivacyPolicy from './PrivacyPolicy';

// ─── Font constants ───────────────────────────────────────────────────────────
const sans = "'DM Sans', system-ui, sans-serif";
const serif = "'DM Serif Display', Georgia, serif";

// ─── Sparkline SVG ───────────────────────────────────────────────────────────
function Sparkline({ values, color }) {
  const width = 120;
  const height = 36;
  const pts = values.filter(v => v !== null);
  if (pts.length < 2) {
    return <svg width={width} height={height}><text x="4" y="22" fontSize="11" fill="#b4b2a9">No data yet</text></svg>;
  }
  const min = Math.min(...pts);
  const max = Math.max(...pts);
  const range = max - min || 1;
  const points = values
    .map((v, i) => {
      if (v === null) return null;
      const x = (i / (values.length - 1)) * (width - 4) + 2;
      const y = height - 4 - ((v - min) / range) * (height - 8);
      return `${x},${y}`;
    })
    .filter(Boolean)
    .join(' ');
  return (
    <svg width={width} height={height} style={{ overflow: 'visible' }}>
      <polyline points={points} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {(() => {
        const lastIdx = values.map((v, i) => v !== null ? i : -1).filter(i => i >= 0).pop();
        if (lastIdx === undefined) return null;
        const x = (lastIdx / (values.length - 1)) * (width - 4) + 2;
        const y = height - 4 - ((values[lastIdx] - min) / range) * (height - 8);
        return <circle cx={x} cy={y} r="3" fill={color} />;
      })()}
    </svg>
  );
}

// ─── Score card with sparkline ────────────────────────────────────────────────
function ScoreCard({ label, avg, values, color }) {
  const trend = values.filter(v => v !== null);
  const recent = trend.slice(-3);
  const earlier = trend.slice(-6, -3);
  const recentAvg = recent.length ? recent.reduce((a, b) => a + b, 0) / recent.length : null;
  const earlierAvg = earlier.length ? earlier.reduce((a, b) => a + b, 0) / earlier.length : null;
  const trendDir = recentAvg && earlierAvg
    ? recentAvg > earlierAvg + 0.3 ? '↑' : recentAvg < earlierAvg - 0.3 ? '↓' : '→'
    : null;
  const trendColor = trendDir === '↑' ? '#7A9E87' : trendDir === '↓' ? '#D4A5A5' : '#6c8396';
  return (
    <div style={scoreCardStyles.card}>
      <div style={scoreCardStyles.label}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', margin: '6px 0 10px' }}>
        <div style={scoreCardStyles.avg}>
          {avg !== null ? avg : '—'}
          {avg !== null && <span style={scoreCardStyles.max}>/10</span>}
        </div>
        {trendDir && <span style={{ ...scoreCardStyles.trend, color: trendColor }}>{trendDir}</span>}
      </div>
      <Sparkline values={values} color={color} />
    </div>
  );
}
const scoreCardStyles = {
  card: { backgroundColor: 'white', borderRadius: '10px', border: '1px solid rgba(44,44,42,0.07)', padding: '14px 16px' },
  label: { fontSize: '11px', color: '#6c8396', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '2px', fontFamily: sans, fontWeight: '600' },
  avg: { fontFamily: serif, fontSize: '24px', fontWeight: '400', color: '#1a1a18', lineHeight: 1 },
  max: { fontFamily: sans, fontSize: '12px', fontWeight: '400', color: '#6c8396' },
  trend: { fontFamily: sans, fontSize: '13px', fontWeight: '600' },
};

// ─── Frequency grid ──────────────────────────────────────────────────────────
function FrequencyGrid({ frequencyGrid }) {
  if (!frequencyGrid || frequencyGrid.length === 0) return null;
  return (
    <div style={gridStyles.wrapper}>
      {frequencyGrid.map(({ symptom, days, frequency }) => {
        const recent = days.slice(-14);
        return (
          <div key={symptom} style={gridStyles.row}>
            <div style={gridStyles.symptomName}>
              {symptom.charAt(0).toUpperCase() + symptom.slice(1)}
              <span style={gridStyles.freq}>{frequency}%</span>
            </div>
            <div style={gridStyles.dots}>
              {recent.map(({ date, logged, severity }) => (
                <div key={date} title={date} style={{
                  ...gridStyles.dot,
                  backgroundColor: logged
                    ? severity >= 4 ? '#D4A5A5' : severity >= 2 ? '#e8c4c4' : '#f0d8d8'
                    : '#f0ede8',
                }} />
              ))}
            </div>
          </div>
        );
      })}
      <div style={gridStyles.legend}>
        {[
          { bg: '#f0ede8', border: '1px solid #e1e8ed', label: 'Not logged' },
          { bg: '#f0d8d8', label: 'Mild' },
          { bg: '#e8c4c4', label: 'Moderate' },
          { bg: '#D4A5A5', label: 'Severe' },
        ].map(({ bg, border, label }) => (
          <div key={label} style={gridStyles.legendItem}>
            <div style={{ ...gridStyles.dot, backgroundColor: bg, border }} />
            <span>{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
const gridStyles = {
  wrapper: { display: 'flex', flexDirection: 'column', gap: '8px' },
  row: { display: 'flex', alignItems: 'center', gap: '10px' },
  symptomName: { fontSize: '13px', color: '#2C2C2A', width: '130px', flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontFamily: sans },
  freq: { fontSize: '11px', color: '#6c8396', fontWeight: '600' },
  dots: { display: 'flex', gap: '3px', flexWrap: 'nowrap' },
  dot: { width: '14px', height: '14px', borderRadius: '3px', flexShrink: 0 },
  legend: { display: 'flex', gap: '14px', marginTop: '8px', flexWrap: 'wrap' },
  legendItem: { display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: '#6c8396', fontFamily: sans },
};

// ─── Week comparison ─────────────────────────────────────────────────────────
function WeekComparison({ weekComparison }) {
  if (!weekComparison || weekComparison.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {weekComparison.map(({ symptom, thisWeek, lastWeek, trend }) => {
        const trendColor = trend < 0 ? '#7A9E87' : trend > 0 ? '#D4A5A5' : '#6c8396';
        const trendLabel = trend > 0 ? `+${trend}d` : trend < 0 ? `${trend}d` : 'same';
        return (
          <div key={symptom} style={weekStyles.row}>
            <div style={weekStyles.name}>{symptom.charAt(0).toUpperCase() + symptom.slice(1)}</div>
            <div style={weekStyles.bars}>
              {[['This week', thisWeek, '#D4A5A5'], ['Last week', lastWeek, '#e1e8ed']].map(([label, val, bg]) => (
                <div key={label} style={weekStyles.barRow}>
                  <div style={weekStyles.barLabel}>{label}</div>
                  <div style={weekStyles.barTrack}>
                    <div style={{ ...weekStyles.barFill, width: `${(val / 7) * 100}%`, backgroundColor: bg }} />
                  </div>
                  <div style={weekStyles.barCount}>{val}d</div>
                </div>
              ))}
            </div>
            <div style={{ ...weekStyles.trend, color: trendColor }}>{trendLabel}</div>
          </div>
        );
      })}
    </div>
  );
}
const weekStyles = {
  row: { backgroundColor: 'white', borderRadius: '10px', border: '1px solid #f0ede8', padding: '12px 14px', display: 'flex', alignItems: 'center', gap: '12px' },
  name: { fontSize: '13px', color: '#2C2C2A', width: '100px', flexShrink: 0, textTransform: 'capitalize', fontFamily: sans },
  bars: { flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' },
  barRow: { display: 'flex', alignItems: 'center', gap: '6px' },
  barLabel: { fontSize: '10px', color: '#6c8396', width: '56px', flexShrink: 0, fontFamily: sans },
  barTrack: { flex: 1, height: '6px', backgroundColor: '#f0ede8', borderRadius: '100px', overflow: 'hidden' },
  barFill: { height: '100%', borderRadius: '100px' },
  barCount: { fontSize: '11px', color: '#6c8396', width: '18px', textAlign: 'right', flexShrink: 0, fontFamily: sans },
  trend: { fontSize: '12px', fontWeight: '700', width: '36px', textAlign: 'right', flexShrink: 0, fontFamily: sans },
};

// ─── Lifestyle correlations panel ─────────────────────────────────────────────
const LIFESTYLE_LABELS = {
  alcohol:     'Alcohol',
  poor_sleep:  'Poor sleep',
  high_stress: 'High stress',
  exercise:    'Exercise',
  caffeine:    'Caffeine',
  travel:      'Travel',
};

function LifestyleCorrelations({ lifestyleCorrelations, lifestyleCounts, daysLogged }) {
  const hasCorrelations = lifestyleCorrelations && Object.keys(lifestyleCorrelations).length > 0;
  const hasCounts = lifestyleCounts && Object.keys(lifestyleCounts).length > 0;

  if (!hasCounts && !hasCorrelations) return (
    <div style={corrStyles.empty}>
      <p style={corrStyles.emptyText}>
        Log lifestyle factors — caffeine, alcohol, sleep quality — when you track, and we'll show you how they connect to your symptoms.
      </p>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {/* Correlation findings */}
      {hasCorrelations && Object.entries(lifestyleCorrelations).map(([tag, data]) => (
        <div key={tag} style={corrStyles.card}>
          <div style={corrStyles.tagHeader}>
            <span style={corrStyles.tagName}>{LIFESTYLE_LABELS[tag] || tag}</span>
            <span style={corrStyles.tagCount}>{data.tagDays} of {data.totalDays} days</span>
          </div>
          {data.findings.map(f => (
            <div key={f.symptomKey} style={corrStyles.finding}>
              <div style={corrStyles.findingSymptom}>
                {f.symptom.charAt(0).toUpperCase() + f.symptom.slice(1)}
              </div>
              <div style={corrStyles.findingBars}>
                <div style={corrStyles.findingBarRow}>
                  <span style={corrStyles.findingBarLabel}>With {LIFESTYLE_LABELS[tag]?.toLowerCase()}</span>
                  <div style={corrStyles.barTrack}>
                    <div style={{ ...corrStyles.barFill, width: `${f.rateOnTag}%`, backgroundColor: '#D4A5A5' }} />
                  </div>
                  <span style={corrStyles.findingPct}>{f.rateOnTag}%</span>
                </div>
                <div style={corrStyles.findingBarRow}>
                  <span style={corrStyles.findingBarLabel}>Other days</span>
                  <div style={corrStyles.barTrack}>
                    <div style={{ ...corrStyles.barFill, width: `${f.rateOffTag}%`, backgroundColor: '#e1e8ed' }} />
                  </div>
                  <span style={corrStyles.findingPct}>{f.rateOffTag}%</span>
                </div>
              </div>
            </div>
          ))}
          <p style={corrStyles.observation}>
            You noticed {data.findings[0].symptom} on {data.findings[0].rateOnTag}% of your {LIFESTYLE_LABELS[tag]?.toLowerCase()} days — vs {data.findings[0].rateOffTag}% of other days.
          </p>
        </div>
      ))}

      {/* Lifestyle frequency — tags with no strong correlation yet */}
      {hasCounts && (
        <div style={corrStyles.countsCard}>
          <div style={corrStyles.countsTitle}>Last 14 days</div>
          {Object.entries(lifestyleCounts)
            .sort(([, a], [, b]) => b - a)
            .map(([key, count]) => {
              const pct = Math.round((count / Math.min(daysLogged, 14)) * 100);
              return (
                <div key={key} style={corrStyles.countRow}>
                  <span style={corrStyles.countLabel}>{LIFESTYLE_LABELS[key] || key}</span>
                  <div style={corrStyles.barTrack}>
                    <div style={{ ...corrStyles.barFill, width: `${pct}%`, backgroundColor: '#D4A5A5' }} />
                  </div>
                  <span style={corrStyles.countDays}>{count}d</span>
                </div>
              );
            })}
          {!hasCorrelations && (
            <p style={corrStyles.moreDataNote}>
              Log for a few more days with these factors and we'll show you which ones connect to your symptoms.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
const corrStyles = {
  empty: { backgroundColor: 'white', borderRadius: '10px', border: '1px solid rgba(44,44,42,0.07)', padding: '20px' },
  emptyText: { fontFamily: sans, fontSize: '14px', color: '#6c8396', lineHeight: '1.6', margin: 0, fontWeight: '300' },
  card: { backgroundColor: 'white', borderRadius: '10px', border: '1px solid rgba(44,44,42,0.07)', padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px' },
  tagHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  tagName: { fontFamily: sans, fontSize: '14px', fontWeight: '600', color: '#2C2C2A' },
  tagCount: { fontFamily: sans, fontSize: '12px', color: '#6c8396' },
  finding: { display: 'flex', flexDirection: 'column', gap: '6px' },
  findingSymptom: { fontFamily: sans, fontSize: '12px', fontWeight: '500', color: '#4a6070', textTransform: 'capitalize' },
  findingBars: { display: 'flex', flexDirection: 'column', gap: '4px' },
  findingBarRow: { display: 'flex', alignItems: 'center', gap: '8px' },
  findingBarLabel: { fontFamily: sans, fontSize: '11px', color: '#6c8396', width: '90px', flexShrink: 0 },
  barTrack: { flex: 1, height: '6px', backgroundColor: '#f0ede8', borderRadius: '100px', overflow: 'hidden' },
  barFill: { height: '100%', borderRadius: '100px' },
  findingPct: { fontFamily: sans, fontSize: '11px', color: '#6c8396', width: '28px', textAlign: 'right', flexShrink: 0 },
  observation: { fontFamily: sans, fontSize: '13px', color: '#4a6070', lineHeight: '1.5', margin: 0, fontWeight: '300', borderTop: '1px solid rgba(44,44,42,0.06)', paddingTop: '10px' },
  countsCard: { backgroundColor: 'white', borderRadius: '10px', border: '1px solid rgba(44,44,42,0.07)', padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px' },
  countsTitle: { fontFamily: sans, fontSize: '11px', fontWeight: '600', color: '#6c8396', textTransform: 'uppercase', letterSpacing: '0.08em' },
  countRow: { display: 'flex', alignItems: 'center', gap: '10px' },
  countLabel: { fontFamily: sans, fontSize: '13px', color: '#2C2C2A', width: '90px', flexShrink: 0 },
  countDays: { fontFamily: sans, fontSize: '12px', color: '#6c8396', width: '24px', textAlign: 'right', flexShrink: 0 },
  moreDataNote: { fontFamily: sans, fontSize: '12px', color: '#9aa9b5', lineHeight: '1.5', margin: 0, fontWeight: '300', borderTop: '1px solid rgba(44,44,42,0.06)', paddingTop: '10px' },
};

// ─── Pro gate card ────────────────────────────────────────────────────────────
function ProCard({ title, description }) {
  return (
    <div style={proStyles.card}>
      <div style={proStyles.badge}>Pro — coming soon</div>
      <h3 style={proStyles.title}>{title}</h3>
      <p style={proStyles.description}>{description}</p>
    </div>
  );
}
const proStyles = {
  card: { backgroundColor: 'white', border: '1px solid rgba(212,165,165,0.25)', borderLeft: '3px solid #D4A5A5', borderRadius: '10px', padding: '20px' },
  badge: { display: 'inline-block', color: '#D4A5A5', fontSize: '11px', fontWeight: '600', padding: '4px 12px', border: '1px solid rgba(212,165,165,0.4)', borderRadius: '100px', letterSpacing: '0.08em', marginBottom: '12px', fontFamily: sans },
  title: { fontFamily: serif, fontSize: '20px', fontWeight: '400', color: '#1a1a18', marginBottom: '8px', margin: '0 0 8px', lineHeight: '1.2' },
  description: { fontFamily: sans, fontSize: '14px', color: '#4a6070', lineHeight: '1.65', margin: 0, fontWeight: '300' },
};

// ─── Journey stage update sheet ───────────────────────────────────────────────
const JOURNEY_OPTIONS = [
  { key: 'noticing_symptoms',   label: 'Something feels wrong',     subtitle: "Noticing changes, not sure what's happening" },
  { key: 'seeking_diagnosis',   label: 'Getting answers',           subtitle: 'Looking for a diagnosis' },
  { key: 'preparing_appointment', label: 'Preparing for an appointment', subtitle: 'Want to walk in with a clear picture' },
  { key: 'started_hrt',         label: 'Just started HRT',          subtitle: "Tracking how it's working" },
  { key: 'adjusting_hrt',       label: 'Adjusting my HRT',          subtitle: 'Finding the right dose or type' },
  { key: 'managing_symptoms',   label: 'Finding my normal',         subtitle: "Maintaining what's working" },
];

function JourneySheet({ current, token, onUpdate, onClose }) {
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(current);
  const [hrtType, setHrtType] = useState('');
  const [hrtStart, setHrtStart] = useState('');
  const needsHrt = ['started_hrt', 'adjusting_hrt'].includes(selected);

  const save = async () => {
    if (!selected || selected === current) { onClose(); return; }
    setSaving(true);
    try {
      const body = { journey_stage: selected };
      if (needsHrt && (hrtType || hrtStart)) {
        body.hrt_details = { type: hrtType, start_month: hrtStart, recent_changes: '' };
      }
      await fetch('/api/save-journey', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      onUpdate(selected);
    } catch (_) {}
    finally { setSaving(false); onClose(); }
  };

  return (
    <div style={sheetStyles.overlay} onClick={onClose}>
      <div style={sheetStyles.sheet} onClick={e => e.stopPropagation()}>
        <div style={sheetStyles.handle} />
        <h3 style={sheetStyles.title}>Update your chapter</h3>
        <p style={sheetStyles.sub}>Where are you in your journey right now?</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
          {JOURNEY_OPTIONS.map(opt => (
            <button key={opt.key} style={{ ...sheetStyles.option, ...(selected === opt.key ? sheetStyles.optionSelected : {}) }} onClick={() => setSelected(opt.key)}>
              <span style={sheetStyles.optionLabel}>{opt.label}</span>
              <span style={sheetStyles.optionSub}>{opt.subtitle}</span>
            </button>
          ))}
        </div>
        {needsHrt && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '16px' }}>
            <input style={sheetStyles.input} placeholder="HRT type (e.g. patches, gel)" value={hrtType} onChange={e => setHrtType(e.target.value)} />
            <input style={sheetStyles.input} placeholder="Month started (e.g. October 2026)" value={hrtStart} onChange={e => setHrtStart(e.target.value)} />
          </div>
        )}
        <button style={sheetStyles.saveBtn} onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Update chapter'}
        </button>
      </div>
    </div>
  );
}
const sheetStyles = {
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(44,44,42,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' },
  sheet: { width: '100%', maxWidth: '560px', backgroundColor: '#FAF7F2', borderRadius: '20px 20px 0 0', padding: '16px 20px 32px', maxHeight: '90vh', overflowY: 'auto' },
  handle: { width: '36px', height: '4px', backgroundColor: 'rgba(44,44,42,0.15)', borderRadius: '100px', margin: '0 auto 20px' },
  title: { fontFamily: serif, fontSize: '22px', fontWeight: '400', color: '#1a1a18', margin: '0 0 6px' },
  sub: { fontFamily: sans, fontSize: '14px', color: '#6c8396', margin: '0 0 16px', fontWeight: '300' },
  option: { background: 'white', border: '1px solid rgba(44,44,42,0.10)', borderRadius: '10px', padding: '12px 14px', textAlign: 'left', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: '2px', width: '100%' },
  optionSelected: { border: '1.5px solid #D4A5A5', backgroundColor: '#fdf8f8' },
  optionLabel: { fontFamily: sans, fontSize: '14px', fontWeight: '500', color: '#2C2C2A' },
  optionSub: { fontFamily: sans, fontSize: '12px', color: '#6c8396', fontWeight: '300' },
  input: { width: '100%', border: '1px solid rgba(44,44,42,0.15)', borderRadius: '8px', padding: '10px 12px', fontFamily: sans, fontSize: '14px', color: '#2C2C2A', backgroundColor: 'white', boxSizing: 'border-box', outline: 'none' },
  saveBtn: { width: '100%', backgroundColor: '#2C2C2A', color: 'white', border: 'none', borderRadius: '10px', padding: '14px', fontSize: '15px', fontWeight: '600', cursor: 'pointer', fontFamily: sans },
};

// ─── Greene domain bar ────────────────────────────────────────────────────────
function GreeneDomainBar({ domain, data }) {
  const domainMeta = {
    psychological: { label: 'Psychological', color: '#9DB8CC' },
    somatic:       { label: 'Somatic',        color: '#C4A882' },
    vasomotor:     { label: 'Vasomotor',      color: '#D4A5A5' },
    sexual:        { label: 'Sexual',          color: '#7A9E87' },
  };
  const meta = domainMeta[domain] || { label: domain, color: '#6c8396' };
  const pct = Math.round((data.frequency || 0) * 100);
  const avg = data.avgSeverity ? data.avgSeverity.toFixed(1) : null;
  return (
    <div style={reportStyles.domainRow}>
      <div style={{ ...reportStyles.domainSwatch, backgroundColor: meta.color }} />
      <div style={reportStyles.domainContent}>
        <div style={reportStyles.domainLabelRow}>
          <span style={reportStyles.domainLabel}>{meta.label}</span>
          <span style={reportStyles.domainPct}>{pct}%</span>
        </div>
        <div style={reportStyles.domainTrack}>
          <div style={{ ...reportStyles.domainFill, width: `${pct}%`, backgroundColor: meta.color }} />
        </div>
        {avg && <span style={reportStyles.domainAvg}>avg severity {avg}/5</span>}
      </div>
    </div>
  );
}

// ─── Pro report display ───────────────────────────────────────────────────────
export const REPORT_DISCLAIMER =
  'Prepared by me from my own daily symptom logs using ApptNote, to support my conversation with my doctor. The wording was drafted by AI from my logs. It may contain mistakes, so I have checked it before sharing. It is not a diagnosis or medical advice.';

export function trendArrow(trend) {
  if (trend === 'improving') return '↑';
  if (trend === 'worsening' || trend === 'declining') return '↓';
  return '→';
}

const formatReportDate = (d) =>
  new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

// ─── Mood trend sparkline for report ─────────────────────────────────────────
function ReportSparkline({ moodSparkline }) {
  if (!moodSparkline || moodSparkline.length < 4) return null;
  const width = 280;
  const height = 48;
  const values = moodSparkline.map(d => d.mood).filter(v => v !== null);
  if (values.length < 4) return null;

  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;

  // Build SVG points from non-null entries only
  const pts = moodSparkline
    .map((d, i) => {
      if (d.mood === null) return null;
      const x = (i / (moodSparkline.length - 1)) * (width - 8) + 4;
      const y = height - 6 - ((d.mood - min) / range) * (height - 12);
      return `${x},${y}`;
    })
    .filter(Boolean)
    .join(' ');

  // Shaded area under the line
  const lastPt = pts.split(' ').pop();
  const [lastX] = lastPt.split(',');
  const areaPoints = `${pts} ${lastX},${height - 2} 4,${height - 2}`;

  return (
    <div style={{ padding: '14px 20px', borderBottom: '1px solid rgba(44,44,42,0.06)' }}>
      <div style={reportStyles.blockTitle}>Mood over the period</div>
      <svg width="100%" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" style={{ display: 'block' }}>
        <defs>
          <linearGradient id="moodGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#D4A5A5" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#D4A5A5" stopOpacity="0.02" />
          </linearGradient>
        </defs>
        <polygon points={areaPoints} fill="url(#moodGrad)" />
        <polyline points={pts} fill="none" stroke="#D4A5A5" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
        <span style={{ fontFamily: sans, fontSize: '10px', color: '#9aa9b5' }}>
          {new Date(moodSparkline[0].date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
        </span>
        <span style={{ fontFamily: sans, fontSize: '10px', color: '#9aa9b5' }}>
          {new Date(moodSparkline[moodSparkline.length - 1].date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
        </span>
      </div>
    </div>
  );
}

// ─── HRT before/after display ─────────────────────────────────────────────────
function HrtComparisonBlock({ hrtComparison }) {
  if (!hrtComparison) return null;
  const { preScores, postScores, symptomChanges, preDays, postDays, hrtStartDate } = hrtComparison;

  const scoreKeys = ['mood', 'energy', 'sleep', 'stress'];
  const scoreLabels = { mood: 'Mood', energy: 'Energy', sleep: 'Sleep', stress: 'Stress' };

  return (
    <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(44,44,42,0.06)' }}>
      <div style={reportStyles.blockTitle}>Before vs after HRT</div>
      <p style={{ fontFamily: sans, fontSize: '11px', color: '#9aa9b5', margin: '0 0 12px' }}>
        Started {new Date(hrtStartDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })} · {preDays}d before · {postDays}d after
      </p>

      {/* Score comparison table */}
      <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr 1fr', gap: '6px 8px', marginBottom: '14px' }}>
        <div />
        <div style={{ fontFamily: sans, fontSize: '10px', fontWeight: '600', color: '#6c8396', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Before</div>
        <div style={{ fontFamily: sans, fontSize: '10px', fontWeight: '600', color: '#7A9E87', textTransform: 'uppercase', letterSpacing: '0.06em' }}>After</div>
        {scoreKeys.map(k => {
          const pre  = preScores[k];
          const post = postScores[k];
          const diff = pre && post ? (post - pre).toFixed(1) : null;
          const improved = diff !== null && parseFloat(diff) > 0.2;
          const worsened = diff !== null && parseFloat(diff) < -0.2;
          return [
            <div key={`${k}-label`} style={{ fontFamily: sans, fontSize: '12px', color: '#4a6070' }}>{scoreLabels[k]}</div>,
            <div key={`${k}-pre`}   style={{ fontFamily: sans, fontSize: '13px', color: '#6c8396', fontWeight: '500' }}>{pre ?? '—'}/10</div>,
            <div key={`${k}-post`}  style={{ fontFamily: sans, fontSize: '13px', color: improved ? '#7A9E87' : worsened ? '#D4A5A5' : '#2C2C2A', fontWeight: '600' }}>
              {post ?? '—'}/10{diff && Math.abs(parseFloat(diff)) > 0.2 ? ` (${parseFloat(diff) > 0 ? '+' : ''}${diff})` : ''}
            </div>,
          ];
        })}
      </div>

      {/* Symptom changes */}
      {symptomChanges.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {symptomChanges.map(c => (
            <div key={c.symptom} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontFamily: sans, fontSize: '12px', color: '#4a6070', flex: 1, textTransform: 'capitalize' }}>{c.symptom}</span>
              <span style={{ fontFamily: sans, fontSize: '11px', color: '#9aa9b5' }}>{c.preFreq}%</span>
              <span style={{ fontFamily: sans, fontSize: '11px', color: '#9aa9b5' }}>→</span>
              <span style={{ fontFamily: sans, fontSize: '12px', fontWeight: '600', color: c.direction === 'improved' ? '#7A9E87' : '#D4A5A5' }}>
                {c.postFreq}%
              </span>
              <span style={{ fontFamily: sans, fontSize: '10px', color: c.direction === 'improved' ? '#7A9E87' : '#D4A5A5' }}>
                {c.direction === 'improved' ? '↓ improved' : '↑ increased'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ProReportDisplay({ report }) {
  const contentRef = useRef(null);
  const [generating, setGenerating] = useState(false);

  const handleDownload = async () => {
    if (!contentRef.current) return;
    setGenerating(true);
    try {
      const canvas = await html2canvas(contentRef.current, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
      const margin = 10;
      const pageW = 210;
      const usableW = pageW - margin * 2;
      const totalImgH = (canvas.height * usableW) / canvas.width;
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: [pageW, totalImgH + margin * 2] });
      markPdfAsAiGenerated(pdf);
      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', margin, margin, usableW, totalImgH);
      pdf.save(`ApptNote-Report-${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (err) {
      console.error('PDF generation error:', err);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div style={reportStyles.wrapper}>
      <div ref={contentRef}>
        <div style={reportStyles.chapterHeader}>
          {report.chapterNumber && <div style={reportStyles.chapterBadge}>Chapter {report.chapterNumber}</div>}
          <div style={reportStyles.chapterTitle}>{report.chapterTitle}</div>
          {report.dateFrom && report.dateTo && (
            <div style={reportStyles.periodLine}>
              Based on {report.daysLogged} days of my own logs · {formatReportDate(report.dateFrom)} – {formatReportDate(report.dateTo)}
            </div>
          )}
        </div>

        {report.stats && (
          <div style={reportStyles.statsBar}>
            {[
              { label: 'Mood', val: report.stats.avgMood },
              { label: 'Energy', val: report.stats.avgEnergy },
              { label: 'Sleep', val: report.stats.avgSleep },
              { label: 'Stress', val: report.stats.avgStress },
            ].map(({ label, val }) => val != null && (
              <div key={label} style={reportStyles.statItem}>
                <span style={reportStyles.statVal}>{Number(val).toFixed(1)}</span>
                <span style={reportStyles.statLabel}>{label} /10</span>
              </div>
            ))}
            {report.moodTrend && (
              <div style={reportStyles.statItem}>
                <span style={{ ...reportStyles.statVal, fontSize: '14px' }}>{trendArrow(report.moodTrend)}</span>
                <span style={reportStyles.statLabel}>Mood trend</span>
              </div>
            )}
          </div>
        )}

        <ReportSparkline moodSparkline={report.moodSparkline} />
        <HrtComparisonBlock hrtComparison={report.hrtComparison} />

        {report.greeneProfile && Object.keys(report.greeneProfile).length > 0 && (
          <div style={reportStyles.block}>
            <div style={reportStyles.blockTitle}>Symptom domains</div>
            <div style={reportStyles.domainList}>
              {Object.entries(report.greeneProfile).map(([domain, data]) => (
                <GreeneDomainBar key={domain} domain={domain} data={data} />
              ))}
            </div>
          </div>
        )}

        {report.summary && (
          <div style={reportStyles.block}>
            <div style={reportStyles.blockTitle}>In my own words</div>
            <p style={reportStyles.summaryText}>{report.summary}</p>
          </div>
        )}

        {report.discussionPoints?.length > 0 && (
          <div style={reportStyles.block}>
            <div style={reportStyles.blockTitle}>What I want to discuss</div>
            <ol style={reportStyles.ol}>
              {report.discussionPoints.map((pt, i) => <li key={i} style={reportStyles.li}>{pt}</li>)}
            </ol>
          </div>
        )}

        {report.patterns && (
          <div style={reportStyles.block}>
            <div style={reportStyles.blockTitle}>What I've noticed</div>
            <p style={reportStyles.summaryText}>
              {Array.isArray(report.patterns) ? report.patterns.join(' ') : report.patterns}
            </p>
          </div>
        )}

        {report.appointmentGoal && (
          <div style={reportStyles.goalBlock}>
            <div style={reportStyles.goalLabel}>What I'm hoping to leave with</div>
            <p style={reportStyles.goalText}>{report.appointmentGoal}</p>
          </div>
        )}

        <div style={reportStyles.disclaimerBlock}>
          <p style={reportStyles.disclaimerText}>{REPORT_DISCLAIMER}</p>
          {report.generatedAt && (
            <p style={reportStyles.disclaimerMeta}>Generated {formatReportDate(report.generatedAt)} · apptnote.com</p>
          )}
        </div>
      </div>

      <button style={reportStyles.downloadBtn} onClick={handleDownload} disabled={generating}>
        {generating ? 'Generating PDF…' : 'Download report as PDF'}
      </button>
    </div>
  );
}

const reportStyles = {
  wrapper: { backgroundColor: 'white', borderRadius: '14px', border: '1px solid rgba(44,44,42,0.08)', overflow: 'hidden' },
  chapterHeader: { backgroundColor: '#faf9f7', padding: '16px 20px', borderBottom: '1px solid rgba(44,44,42,0.06)' },
  chapterBadge: { display: 'inline-block', color: '#D4A5A5', fontSize: '10px', fontWeight: '600', padding: '3px 10px', border: '1px solid rgba(212,165,165,0.4)', borderRadius: '100px', letterSpacing: '0.08em', marginBottom: '6px', fontFamily: sans },
  chapterTitle: { fontFamily: serif, fontSize: '18px', fontWeight: '400', color: '#1a1a18', lineHeight: '1.2' },
  periodLine: { fontFamily: sans, fontSize: '12px', color: '#6c8396', marginTop: '6px' },
  statsBar: { display: 'flex', justifyContent: 'space-around', padding: '14px 20px', borderBottom: '1px solid rgba(44,44,42,0.06)', backgroundColor: '#fafaf8' },
  statItem: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' },
  statVal: { fontFamily: serif, fontSize: '18px', fontWeight: '400', color: '#1a1a18' },
  statLabel: { fontFamily: sans, fontSize: '10px', color: '#6c8396', textTransform: 'uppercase', letterSpacing: '0.08em' },
  block: { padding: '16px 20px', borderBottom: '1px solid rgba(44,44,42,0.06)' },
  blockTitle: { fontFamily: sans, fontSize: '11px', fontWeight: '600', color: '#6c8396', textTransform: 'uppercase', letterSpacing: '0.10em', marginBottom: '10px' },
  summaryText: { fontFamily: sans, fontSize: '14px', color: '#2C2C2A', lineHeight: '1.65', margin: 0 },
  domainList: { display: 'flex', flexDirection: 'column', gap: '10px' },
  domainRow: { display: 'flex', alignItems: 'flex-start', gap: '10px' },
  domainSwatch: { width: '10px', height: '10px', borderRadius: '50%', flexShrink: 0, marginTop: '4px' },
  domainContent: { flex: 1 },
  domainLabelRow: { display: 'flex', justifyContent: 'space-between', marginBottom: '4px' },
  domainLabel: { fontFamily: sans, fontSize: '13px', fontWeight: '500', color: '#2C2C2A' },
  domainPct: { fontFamily: sans, fontSize: '12px', color: '#6c8396' },
  domainTrack: { height: '4px', backgroundColor: 'rgba(44,44,42,0.08)', borderRadius: '100px', overflow: 'hidden', marginBottom: '3px' },
  domainFill: { height: '100%', borderRadius: '100px' },
  domainAvg: { fontFamily: sans, fontSize: '11px', color: '#6c8396' },
  ol: { margin: 0, paddingLeft: '20px' },
  li: { fontFamily: sans, fontSize: '14px', color: '#2C2C2A', lineHeight: '1.6', marginBottom: '6px' },
  goalBlock: { backgroundColor: '#f7fbf8', padding: '16px 20px', borderBottom: '1px solid rgba(44,44,42,0.06)' },
  goalLabel: { fontFamily: sans, fontSize: '11px', fontWeight: '600', color: '#7A9E87', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px' },
  goalText: { fontFamily: sans, fontSize: '14px', color: '#2C2C2A', lineHeight: '1.6', margin: 0 },
  disclaimerBlock: { padding: '14px 20px', backgroundColor: '#faf9f7' },
  disclaimerText: { fontFamily: sans, fontSize: '11px', color: '#6c8396', lineHeight: '1.6', margin: 0 },
  disclaimerMeta: { fontFamily: sans, fontSize: '10px', color: '#9aa9b5', margin: '4px 0 0', letterSpacing: '0.02em' },
  downloadBtn: { display: 'block', width: '100%', padding: '14px 20px', backgroundColor: 'white', color: '#6c8396', border: 'none', borderTop: '1px solid rgba(44,44,42,0.06)', fontSize: '13px', fontWeight: '500', cursor: 'pointer', fontFamily: sans, textAlign: 'center' },
  generateBtn: { width: '100%', backgroundColor: '#2C2C2A', color: 'white', border: 'none', borderRadius: '10px', padding: '14px 20px', fontSize: '15px', fontWeight: '600', cursor: 'pointer', fontFamily: sans },
  loadingText: { fontFamily: sans, fontSize: '14px', color: '#6c8396', textAlign: 'center', padding: '20px 0' },
  errorText: { fontFamily: sans, fontSize: '14px', color: '#c44', textAlign: 'center', padding: '12px 0' },
};

// ─── Tab bar ──────────────────────────────────────────────────────────────────
function TabBar({ active, onChange }) {
  const tabs = [
    { id: 'trends',     label: 'Trends' },
    { id: 'patterns',   label: 'Patterns' },
    { id: 'appointment', label: 'Appointment' },
  ];
  return (
    <div style={tabStyles.bar}>
      {tabs.map(t => (
        <button
          key={t.id}
          style={{ ...tabStyles.tab, ...(active === t.id ? tabStyles.tabActive : {}) }}
          onClick={() => onChange(t.id)}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}
const tabStyles = {
  bar: { display: 'flex', backgroundColor: 'rgba(44,44,42,0.05)', borderRadius: '10px', padding: '3px', marginBottom: '20px' },
  tab: { flex: 1, padding: '8px 4px', border: 'none', background: 'none', borderRadius: '8px', fontFamily: sans, fontSize: '13px', fontWeight: '500', color: '#6c8396', cursor: 'pointer', transition: 'all 0.15s ease' },
  tabActive: { backgroundColor: 'white', color: '#2C2C2A', fontWeight: '600', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
};

// ─── Date range toggle ────────────────────────────────────────────────────────
function DateRangeToggle({ value, onChange, isHRTUser }) {
  const options = [
    { id: 14,  label: '14 days' },
    { id: 30,  label: '30 days' },
    { id: 60,  label: '60 days' },
    ...(isHRTUser ? [{ id: 'hrt', label: 'Since HRT' }] : []),
  ];
  return (
    <div style={rangeStyles.row}>
      {options.map(opt => (
        <button
          key={opt.id}
          style={{ ...rangeStyles.btn, ...(value === opt.id ? rangeStyles.btnActive : {}) }}
          onClick={() => onChange(opt.id)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}
const rangeStyles = {
  row: { display: 'flex', gap: '6px', marginBottom: '16px', flexWrap: 'wrap' },
  btn: { padding: '6px 14px', border: '1px solid rgba(44,44,42,0.12)', borderRadius: '100px', background: 'white', fontFamily: sans, fontSize: '12px', fontWeight: '500', color: '#6c8396', cursor: 'pointer' },
  btnActive: { backgroundColor: '#2C2C2A', color: 'white', border: '1px solid #2C2C2A' },
};

// ─── Main ReportPage ─────────────────────────────────────────────────────────
function ReportPage({ userEmail, token, journeyStage: initialJourneyStage, onBack }) {
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('trends');
  const [showJourneySheet, setShowJourneySheet] = useState(false);
  const [journeyStage, setJourneyStage] = useState(initialJourneyStage);
  const [patternRange, setPatternRange] = useState(30);

  // Report state (Appointment tab)
  const [proReport, setProReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState(null);
  const [freeLimitUsed, setFreeLimitUsed] = useState(false);
  const [previousReportDate, setPreviousReportDate] = useState(null);

  const [showWaitlist, setShowWaitlist] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);

  const isFounder = ['hodnetttania@gmail.com', 'taniahodnett@yahoo.ie'].includes(userEmail);
  const isHRTUser = ['started_hrt', 'adjusting_hrt'].includes(journeyStage);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchTrends(); }, []);

  const fetchTrends = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/get-trends', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setTrends(data);
    } catch (err) {
      setError('Could not load your data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Which date window to use for the report (synced with patternRange)
  const reportDays = patternRange; // 14 | 30 | 60 | 'hrt'

  const fetchProReport = async () => {
    setReportLoading(true);
    setReportError(null);
    try {
      const res = await fetch('/api/generate-report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ days: reportDays }),
      });
      const data = await res.json();
      if (res.status === 400 && data.error === 'not_enough_data') {
        setReportError(data.message);
        return;
      }
      if (res.status === 403 && data.error === 'free_limit_reached') {
        setFreeLimitUsed(true);
        setPreviousReportDate(data.generatedAt);
        // If the API returned the saved report content, show it
        if (data.savedReport) {
          const r = data.savedReport;
          if (r.patterns && !Array.isArray(r.patterns)) r.patterns = [r.patterns];
          setProReport(r);
        }
        return;
      }
      if (!res.ok) throw new Error(data.error);
      const r = data.report ?? data;
      if (r.patterns && !Array.isArray(r.patterns)) r.patterns = [r.patterns];
      setProReport(r);
    } catch (err) {
      setReportError('Could not generate your report. Please try again.');
    } finally {
      setReportLoading(false);
    }
  };

  const formatDate = (d) => new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

  const JOURNEY_LABELS = {
    noticing_symptoms: 'Chapter 1 — Something feels wrong',
    seeking_diagnosis: 'Chapter 1 — Getting answers',
    preparing_appointment: 'Chapter 2 — Preparing for appointment',
    started_hrt: 'Chapter 3 — Just started HRT',
    adjusting_hrt: 'Chapter 3 — Adjusting HRT',
    managing_symptoms: 'Chapter 4 — Finding my normal',
  };

  // ── Loading ──────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingState}>
          <div style={styles.loadingDots}>
            {[0, 1, 2].map(i => <div key={i} style={{ ...styles.dot, animationDelay: `${i * 0.15}s` }} />)}
          </div>
          <p style={styles.loadingText}>Loading your data…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <p style={styles.errorText}>{error}</p>
        <button style={styles.retryBtn} onClick={fetchTrends}>Try again</button>
      </div>
    );
  }

  if (!trends || trends.empty || trends.daysLogged < 3) {
    return (
      <div style={styles.container}>
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>📋</div>
          <h2 style={styles.emptyTitle}>Your insights will appear here</h2>
          <p style={styles.emptyText}>
            Log for at least 3 days and your trends and your free appointment report will be available here.
          </p>
          <div style={styles.emptyProgress}>
            <div style={styles.emptyProgressFill({ pct: ((trends?.daysLogged || 0) / 3) * 100 })} />
          </div>
          <p style={styles.emptyProgressLabel}>{trends?.daysLogged || 0} of 3 days logged</p>
        </div>
      </div>
    );
  }

  // ── Chapter banner ────────────────────────────────────────────────────────
  const chapterBanner = (
    <div style={styles.chapterBanner}>
      <div style={styles.chapterBannerLeft}>
        <span style={styles.chapterBannerLabel}>Your chapter</span>
        <span style={styles.chapterBannerValue}>{JOURNEY_LABELS[journeyStage] || 'Your journey'}</span>
      </div>
      <button style={styles.chapterUpdateBtn} onClick={() => setShowJourneySheet(true)}>
        Update
      </button>
    </div>
  );

  // ── Header stats ──────────────────────────────────────────────────────────
  const headerStats = (
    <div style={styles.headerStats}>
      <div style={styles.statPill}>
        <span style={styles.statValue}>{trends.daysLogged}</span>
        <span style={styles.statLabel}>days logged</span>
      </div>
      <div style={styles.statPill}>
        <span style={styles.statValue}>{trends.streak}</span>
        <span style={styles.statLabel}>day streak</span>
      </div>
      <div style={styles.statPill}>
        <span style={styles.statLabel}>
          {formatDate(trends.dateFrom)} — {formatDate(trends.dateTo)}
        </span>
      </div>
    </div>
  );

  // ── TRENDS tab ────────────────────────────────────────────────────────────
  const trendsTab = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Wellbeing sparklines */}
      <div>
        <h2 style={styles.sectionTitle}>Wellbeing — last 14 days</h2>
        <div style={styles.sparklineGrid}>
          {[
            { label: 'Mood',   key: 'mood',   color: '#D4A5A5' },
            { label: 'Energy', key: 'energy', color: '#7A9E87' },
            { label: 'Sleep',  key: 'sleep',  color: '#9DB8CC' },
            { label: 'Stress', key: 'stress', color: '#C4A882' },
          ].map(({ label, key, color }) => (
            <ScoreCard key={key} label={label} avg={trends.averages[key]} values={trends.sparklines[key]} color={color} />
          ))}
        </div>
      </div>

      {/* Symptom frequency grid */}
      {trends.frequencyGrid?.length > 0 && (
        <div>
          <h2 style={styles.sectionTitle}>Symptom frequency — last 30 days</h2>
          <FrequencyGrid frequencyGrid={trends.frequencyGrid} />
        </div>
      )}

      {/* This week vs last */}
      {trends.weekComparison?.length > 0 && (
        <div>
          <h2 style={styles.sectionTitle}>This week vs last week</h2>
          <WeekComparison weekComparison={trends.weekComparison} />
        </div>
      )}
    </div>
  );

  // ── PATTERNS tab ──────────────────────────────────────────────────────────
  const patternsTab = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Date range toggle */}
      <div>
        <h2 style={styles.sectionTitle}>Analysis window</h2>
        <DateRangeToggle value={patternRange} onChange={setPatternRange} isHRTUser={isHRTUser} />
        <p style={styles.rangeNote}>
          {patternRange === 'hrt'
            ? 'Showing patterns since you started HRT.'
            : `Showing patterns across your last ${patternRange} days of logs.`}
        </p>
      </div>

      {/* Lifestyle connections */}
      <div>
        <h2 style={styles.sectionTitle}>Lifestyle connections</h2>
        <LifestyleCorrelations
          lifestyleCorrelations={trends.lifestyleCorrelations}
          lifestyleCounts={trends.lifestyleCounts}
          daysLogged={trends.daysLogged}
        />
      </div>

      {/* HRT before/after — Pro */}
      {isHRTUser && (
        <div>
          <h2 style={styles.sectionTitle}>Before vs after HRT</h2>
          <ProCard
            title="How has HRT changed things?"
            description="A week-by-week comparison of your symptoms before and after you started treatment. Shows how your logged scores have changed since you started, side by side."
          />
        </div>
      )}

      {/* AI pattern insights — Pro */}
      <div>
        <h2 style={styles.sectionTitle}>Your patterns, pulled together</h2>
        <ProCard
          title="AI pattern summary"
          description={`You've logged ${trends.daysLogged} days of data. Pro pulls your logs into a clear summary so you know what to raise with your doctor.`}
        />
      </div>
    </div>
  );

  // ── APPOINTMENT tab ───────────────────────────────────────────────────────
  const appointmentTab = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <p style={styles.appointmentIntro}>
        Generate a structured summary of your symptoms — written in the first person, ready to bring to your next appointment so nothing gets forgotten or dismissed.
      </p>

      <>
          {!proReport && !reportLoading && !freeLimitUsed && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <DateRangeToggle value={patternRange} onChange={setPatternRange} isHRTUser={isHRTUser} />
              <button style={reportStyles.generateBtn} onClick={fetchProReport}>
                Generate report — {patternRange === 'hrt' ? 'since HRT' : `last ${patternRange} days`}
              </button>
              {!isFounder && (
                <p style={styles.reportDisclosure}>
                  <strong>Your free report is available until 1 December 2026,</strong> when ApptNote closes. You need at least 3 days of logs. Download it as a PDF to keep it.
                </p>
              )}
              <p style={styles.reportDisclosure}>
                <strong>Your summary is drafted by AI from your logs.</strong> Read it through and correct anything that isn't right before you share it. Your report is saved to your account so you can retrieve it anytime. Your personal notes are never sent to the AI.{' '}
                <button onClick={() => setShowPrivacy(true)} style={styles.reportDisclosureBtn}>Privacy policy</button>
              </p>
            </div>
          )}
          {reportLoading && <p style={reportStyles.loadingText}>Building your report…</p>}
          {reportError && <p style={reportStyles.errorText}>{reportError}</p>}
          {freeLimitUsed && (
            <div style={{ ...proStyles.card }}>
              <p style={{ fontFamily: sans, fontSize: '14px', color: '#4a6070', margin: 0 }}>
                You've used your free beta report
                {previousReportDate ? ` (created ${formatDate(previousReportDate)})` : ''}.
                {proReport ? ' Here it is again.' : ''} More reports will come with ApptNote Pro.
              </p>
            </div>
          )}
          {proReport && <ProReportDisplay report={proReport} />}
      </>
    </div>
  );

  return (
    <div style={styles.container}>
      {chapterBanner}
      {headerStats}

      <TabBar active={activeTab} onChange={setActiveTab} />

      {activeTab === 'trends'      && trendsTab}
      {activeTab === 'patterns'    && patternsTab}
      {activeTab === 'appointment' && appointmentTab}

      {showJourneySheet && (
        <JourneySheet
          current={journeyStage}
          token={token}
          onUpdate={(stage) => setJourneyStage(stage)}
          onClose={() => setShowJourneySheet(false)}
        />
      )}

      {showPrivacy && <PrivacyPolicy onClose={() => setShowPrivacy(false)} />}

      {showWaitlist && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalInner}>
            <WaitlistModal onClose={() => setShowWaitlist(false)} context="pro" />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────
const styles = {
  container: { paddingBottom: '40px' },

  // Loading
  loadingState: { textAlign: 'center', padding: '60px 0' },
  loadingDots: { display: 'flex', gap: '8px', justifyContent: 'center', marginBottom: '16px' },
  dot: { width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#D4A5A5', animation: 'blink 1.2s infinite ease-in-out' },
  loadingText: { fontSize: '14px', color: '#6c8396', fontFamily: sans },

  // Error
  errorText: { fontSize: '14px', color: '#c44', textAlign: 'center', marginBottom: '12px', fontFamily: sans },
  retryBtn: { display: 'block', margin: '0 auto', padding: '10px 24px', backgroundColor: '#7A9E87', color: 'white', border: 'none', borderRadius: '10px', fontSize: '14px', cursor: 'pointer', fontFamily: 'inherit' },

  // Empty state
  emptyState: { textAlign: 'center', padding: '56px 24px' },
  emptyIcon: { fontSize: '40px', marginBottom: '16px' },
  emptyTitle: { fontFamily: serif, fontSize: '26px', fontWeight: '400', color: '#1a1a18', marginBottom: '12px' },
  emptyText: { fontFamily: sans, fontSize: '15px', color: '#4a6070', lineHeight: '1.65', fontWeight: '300', marginBottom: '24px', maxWidth: '340px', margin: '0 auto 24px' },
  emptyProgress: { height: '6px', backgroundColor: '#f0ede8', borderRadius: '100px', overflow: 'hidden', maxWidth: '200px', margin: '0 auto 8px' },
  emptyProgressFill: ({ pct }) => ({ height: '100%', width: `${Math.min(pct, 100)}%`, backgroundColor: '#7A9E87', borderRadius: '100px' }),
  emptyProgressLabel: { fontSize: '13px', color: '#6c8396', fontFamily: sans },

  // Chapter banner
  chapterBanner: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: 'white', borderRadius: '10px', border: '1px solid rgba(44,44,42,0.07)', padding: '12px 16px', marginBottom: '16px' },
  chapterBannerLeft: { display: 'flex', flexDirection: 'column', gap: '2px' },
  chapterBannerLabel: { fontFamily: sans, fontSize: '10px', fontWeight: '600', color: '#6c8396', textTransform: 'uppercase', letterSpacing: '0.08em' },
  chapterBannerValue: { fontFamily: sans, fontSize: '13px', color: '#2C2C2A', fontWeight: '500' },
  chapterUpdateBtn: { background: 'none', border: '1px solid rgba(44,44,42,0.12)', borderRadius: '100px', padding: '5px 14px', fontFamily: sans, fontSize: '12px', fontWeight: '500', color: '#6c8396', cursor: 'pointer' },

  // Header stats
  headerStats: { display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '20px' },
  statPill: { backgroundColor: 'white', border: '1px solid rgba(44,44,42,0.08)', borderRadius: '100px', padding: '8px 16px', display: 'flex', alignItems: 'center', gap: '6px' },
  statValue: { fontFamily: serif, fontSize: '20px', fontWeight: '400', color: '#1a1a18' },
  statLabel: { fontFamily: sans, fontSize: '12px', color: '#6c8396', fontWeight: '400' },

  // Section headings
  sectionTitle: { fontFamily: sans, fontSize: '11px', fontWeight: '600', color: '#6c8396', textTransform: 'uppercase', letterSpacing: '0.10em', marginBottom: '12px', marginTop: 0 },

  // Sparkline grid
  sparklineGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' },

  // Range note
  rangeNote: { fontFamily: sans, fontSize: '12px', color: '#9aa9b5', margin: 0, fontWeight: '300' },

  // Appointment intro
  appointmentIntro: { fontFamily: sans, fontSize: '14px', color: '#4a6070', lineHeight: '1.65', margin: 0, fontWeight: '300' },

  // Report disclosure
  reportDisclosure: { fontFamily: sans, fontSize: '12px', color: '#9aa9b5', lineHeight: '1.5', margin: 0, fontWeight: '300', textAlign: 'center' },
  reportDisclosureLink: { color: '#9aa9b5', textDecoration: 'underline' },
  reportDisclosureBtn: { background: 'none', border: 'none', padding: 0, color: '#9aa9b5', textDecoration: 'underline', cursor: 'pointer', fontSize: '12px', fontFamily: "'DM Sans', system-ui, sans-serif" },

  // Modal
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(44,44,42,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' },
  modalInner: { width: '100%', maxWidth: '560px', backgroundColor: 'white', borderRadius: '20px 20px 0 0', maxHeight: '90vh', overflowY: 'auto' },
};

export default ReportPage;
